import { renderSlot as _renderSlot, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, isRef as _isRef, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20 max-w-6xl" }
const _hoisted_2 = { class: "mb-6 flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "text-sm text-gray-500" }
const _hoisted_4 = {
  key: 0,
  class: "mb-6 text-sm text-red-600"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white" }
const _hoisted_7 = { class: "mb-6 rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_8 = { class: "flex flex-wrap items-stretch gap-2" }
const _hoisted_9 = ["data-step"]
const _hoisted_10 = { class: "text-xs uppercase tracking-wider text-gray-500" }
const _hoisted_11 = { class: "mt-1 text-xl font-bold text-gray-900" }
const _hoisted_12 = { class: "min-h-[1rem] text-xs text-gray-500" }
const _hoisted_13 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm mb-8" }
const _hoisted_14 = { class: "p-4 pb-2" }
const _hoisted_15 = { class: "text-lg font-bold text-gray-900" }
const _hoisted_16 = { class: "text-sm text-gray-500 mt-0.5" }
const _hoisted_17 = { class: "px-4 pb-4 h-72" }
const _hoisted_18 = {
  key: 0,
  class: "overflow-x-auto p-2 -m-2"
}
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { class: "truncate text-gray-900" }
const _hoisted_21 = { class: "truncate text-xs text-gray-500" }
const _hoisted_22 = { class: "text-gray-700" }
const _hoisted_23 = {
  key: 0,
  class: "text-xs text-gray-400"
}
const _hoisted_24 = {
  key: 0,
  class: "text-gray-700"
}
const _hoisted_25 = { class: "text-gray-700" }
const _hoisted_26 = { class: "text-gray-700" }
const _hoisted_27 = { class: "flex justify-end" }
const _hoisted_28 = { class: "text-sm text-gray-500 mt-2" }
const _hoisted_29 = {
  key: 2,
  class: "mt-4 text-center"
}
const _hoisted_30 = { class: "mt-10" }

import 'chart.js/auto'

import { ChevronRightIcon } from '@heroicons/vue/20/solid'
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'

import EmptyState from '@/components/neue/EmptyState.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueTablist from '@/components/neue/NeueTablist.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import ReportDownloadHistory from '@/components/reports/ReportDownloadHistory.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import SecondaryTop from '@/components/SecondaryTop.vue'
import { useLeadsReport } from '@/composables/useLeadsReport'
import AgentConversationsTile from '@/view/ai/AgentConversationsTile.vue'
import {
  chartData,
  conversionRate,
  formatMoney,
  funnelSteps,
  LEAD_STATUS_KEYS,
  ratio,
  statusFor
} from '@/view/ai/websiteReport'


export default {
  __name: 'LeadsReport',
  props: {
  title: { type: String, required: true },
  parent: { type: Object, default: undefined },
  endpoint: { type: String, required: true },
  params: { type: Object, default: () => ({}) },
  downloadType: { type: String, required: true },
  downloadParams: { type: Object, default: () => ({}) },
  description: { type: String, required: true },
  emptyText: { type: String, required: true },
  wording: { type: Object, default: () => ({ date: 'Inquired', plural: 'inquiries', verb: 'inquired' }) },
  tiles: { type: Array, default: () => ['leads', 'paid_orders', 'revenue', 'conversion'] },
  statuses: { type: Array, default: () => LEAD_STATUS_KEYS }
},
  setup(__props) {

const props = __props

const { rangeKey, rangeOptions, loading, loadingMore, error, report, rows, hasMore, dateRange, loadMore } =
  useLeadsReport({ endpoint: () => props.endpoint, params: () => props.params })

const current = computed(() => report.value?.tiles?.current ?? {})
const prior = computed(() => report.value?.tiles?.prior ?? {})
const steps = computed(() => funnelSteps(report.value?.funnel ?? []))
const chart = computed(() => chartData(report.value?.periods ?? [], report.value?.grouping, props.statuses))
const downloadRange = computed(() => ({ ...dateRange.value, ...props.downloadParams }))

const plural = (count, word) => (count === 1 ? word : `${word}s`)

const change = (now, before) => ratio(now, before) ?? undefined

const conversionValue = computed(() => {
  const rate = conversionRate(current.value)
  return rate === null ? '-' : `${rate}%`
})

const conversionDelta = computed(() => {
  const now = conversionRate(current.value)
  const before = conversionRate(prior.value)
  return now === null || before === null ? undefined : now - before
})

const countsUnpaidOrders = computed(() => props.statuses.includes('unpaid_order'))

const tileCatalog = computed(() => {
  const now = current.value
  const before = prior.value
  const leadCount = { value: String(now.leads ?? 0), delta: change(now.leads, before.leads) }
  return {
    leads: { ...leadCount, label: 'Leads', caption: `${now.new_leads ?? 0} new ${plural(now.new_leads, 'customer')}` },
    signups: { ...leadCount, label: 'Signups' },
    new_vs_existing: {
      label: 'New vs existing',
      value: `${now.new_leads ?? 0} new`,
      caption: `${Math.max((now.leads ?? 0) - (now.new_leads ?? 0), 0)} existing`
    },
    paid_orders: {
      label: 'Paid orders',
      value: String(now.paid_orders ?? 0),
      delta: change(now.paid_orders, before.paid_orders),
      caption: countsUnpaidOrders.value ? `from ${now.orders ?? 0} ${plural(now.orders, 'order')}` : ''
    },
    revenue: {
      label: 'Revenue',
      value: formatMoney(now.revenue_cents),
      delta: change(now.revenue_cents, before.revenue_cents)
    },
    conversion: {
      label: 'Conversion',
      value: conversionValue.value,
      delta: conversionDelta.value,
      deltaUnit: 'pp',
      caption: 'of leads paid'
    }
  }
})

const visibleTiles = computed(() =>
  props.tiles.filter(key => key in tileCatalog.value).map(key => ({ key, ...tileCatalog.value[key] }))
)

const TILE_GRID_COLS_CLASS = { 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' }
const tileGridColsClass = computed(() => TILE_GRID_COLS_CLASS[visibleTiles.value.length] ?? TILE_GRID_COLS_CLASS[4])

const columns = computed(() =>
  [
    { width: 'minmax(150px, 2fr)' },
    { width: 'minmax(120px, 1fr)' },
    { width: '110px' },
    { width: 'minmax(130px, 1fr)' },
    countsUnpaidOrders.value && { width: '70px' },
    { width: '100px' },
    { width: 'minmax(80px, 1fr)' },
    { width: '64px' }
  ].filter(Boolean)
)

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'top' } },
  scales: {
    x: { stacked: true },
    y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Leads' } }
  }
}

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createBlock(SecondaryNav, null, _createSlots({
    top: _withCtx(() => [
      _createVNode(SecondaryTop, {
        text: __props.title,
        parent: __props.parent
      }, null, 8, ["text", "parent"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(__props.description), 1),
          _createVNode(SelectInput, {
            modelValue: _unref(rangeKey),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => (_isRef(rangeKey) ? (rangeKey).value = $event : null)),
            slim: "",
            options: _unref(rangeOptions).map(opt => ({ name: opt.label, value: opt.value }))
          }, null, 8, ["modelValue", "options"])
        ]),
        (_unref(error))
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(error)), 1))
          : _createCommentVNode("", true),
        _createVNode(NeueLoading, {
          loading: _unref(loading) && !_unref(report)
        }, {
          default: _withCtx(() => [
            (_unref(report))
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["grid grid-cols-1 divide-y divide-gray-200 lg:divide-y-0 lg:divide-x", tileGridColsClass.value])
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleTiles.value, (tile) => {
                        return (_openBlock(), _createBlock(AgentConversationsTile, {
                          key: tile.key,
                          flush: "",
                          "data-tile": tile.key,
                          label: tile.label,
                          value: tile.value,
                          delta: tile.delta,
                          "delta-unit": tile.deltaUnit,
                          caption: tile.caption
                        }, null, 8, ["data-tile", "label", "value", "delta", "delta-unit", "caption"]))
                      }), 128))
                    ], 2)
                  ]),
                  _renderSlot(_ctx.$slots, "highlights", {
                    current: current.value,
                    prior: prior.value,
                    change: change
                  }),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(steps.value, (step, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: step.key
                        }, [
                          index
                            ? (_openBlock(), _createBlock(_unref(ChevronRightIcon), {
                                key: 0,
                                class: "h-4 w-4 shrink-0 self-center text-gray-300",
                                "aria-hidden": "true"
                              }))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: "min-w-[96px] flex-1 rounded-md bg-gray-50 px-3 py-2",
                            "data-step": step.key
                          }, [
                            _createElementVNode("div", _hoisted_10, _toDisplayString(step.label), 1),
                            _createElementVNode("div", _hoisted_11, _toDisplayString(step.count), 1),
                            _createElementVNode("div", _hoisted_12, _toDisplayString(step.share === null ? '' : `${step.share}% of previous`), 1)
                          ], 8, _hoisted_9)
                        ], 64))
                      }), 128))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("h3", _hoisted_15, " Leads by " + _toDisplayString(_unref(report).grouping === 'week' ? 'week' : 'day'), 1),
                      _createElementVNode("p", _hoisted_16, " Each lead sits on the day they " + _toDisplayString(__props.wording.verb) + ", colored by where they ended up. ", 1)
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      (chart.value)
                        ? (_openBlock(), _createBlock(_unref(Bar), {
                            key: 0,
                            data: chart.value,
                            options: chartOptions
                          }, null, 8, ["data"]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  (_unref(rows).length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                        _createVNode(NeueTablist, null, {
                          head: _withCtx(() => [
                            _createVNode(TablistHeader, null, {
                              default: _withCtx(() => [
                                _createVNode(TablistGrid, {
                                  columns: columns.value,
                                  header: "",
                                  "data-lead-header": ""
                                }, {
                                  default: _withCtx(() => [
                                    _cache[1] || (_cache[1] = _createElementVNode("div", null, "Customer", -1)),
                                    _createElementVNode("div", null, _toDisplayString(__props.wording.date), 1),
                                    _cache[2] || (_cache[2] = _createElementVNode("div", null, "Customer type", -1)),
                                    _cache[3] || (_cache[3] = _createElementVNode("div", null, "Status", -1)),
                                    (countsUnpaidOrders.value)
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_19, "Orders"))
                                      : _createCommentVNode("", true),
                                    _cache[4] || (_cache[4] = _createElementVNode("div", null, "Paid orders", -1)),
                                    _cache[5] || (_cache[5] = _createElementVNode("div", null, "Revenue", -1)),
                                    _cache[6] || (_cache[6] = _createElementVNode("div", null, null, -1))
                                  ]),
                                  _: 1
                                }, 8, ["columns"])
                              ]),
                              _: 1
                            })
                          ]),
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rows), (row) => {
                              return (_openBlock(), _createBlock(TablistRow, {
                                key: row.customer_id,
                                "data-lead-row": ""
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(TablistGrid, {
                                    columns: columns.value,
                                    class: "items-center"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", null, [
                                        _createElementVNode("div", _hoisted_20, _toDisplayString(row.name || 'No name yet'), 1),
                                        _createElementVNode("div", _hoisted_21, _toDisplayString(row.phone), 1)
                                      ]),
                                      _createElementVNode("div", _hoisted_22, [
                                        _createTextVNode(_toDisplayString(formatDate(row.last_inquiry_at)) + " ", 1),
                                        (row.inquiries > 1)
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_23, " (" + _toDisplayString(row.inquiries) + " " + _toDisplayString(__props.wording.plural) + ") ", 1))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _createElementVNode("div", null, [
                                        _createElementVNode("span", {
                                          class: _normalizeClass(["px-2 text-xs leading-5 font-semibold rounded-full", row.returning ? 'bg-gray-100 text-gray-700' : 'bg-green-50 text-green-700'])
                                        }, _toDisplayString(row.returning ? 'Returning' : 'New'), 3)
                                      ]),
                                      _createElementVNode("div", null, [
                                        _createElementVNode("span", {
                                          class: _normalizeClass(["px-2 text-xs leading-5 font-semibold rounded-full", _unref(statusFor)(row.status).pill])
                                        }, _toDisplayString(_unref(statusFor)(row.status).label), 3)
                                      ]),
                                      (countsUnpaidOrders.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(row.orders), 1))
                                        : _createCommentVNode("", true),
                                      _createElementVNode("div", _hoisted_25, _toDisplayString(row.paid_orders), 1),
                                      _createElementVNode("div", _hoisted_26, _toDisplayString(row.revenue_cents ? _unref(formatMoney)(row.revenue_cents) : '-'), 1),
                                      _createElementVNode("div", _hoisted_27, [
                                        (row.conversation_user_pair_id)
                                          ? (_openBlock(), _createBlock(_component_router_link, {
                                              key: 0,
                                              to: `/messages/${row.conversation_user_pair_id}`,
                                              class: "text-sm font-medium text-brand-600 hover:text-brand-700"
                                            }, {
                                              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                _createTextVNode(" View → ", -1)
                                              ]))]),
                                              _: 1
                                            }, 8, ["to"]))
                                          : _createCommentVNode("", true)
                                      ])
                                    ]),
                                    _: 2
                                  }, 1032, ["columns"])
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        })
                      ]))
                    : (_openBlock(), _createBlock(NeueWell, {
                        key: 1,
                        class: "text-center"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(EmptyState, { plural: "leads" }),
                          _createElementVNode("p", _hoisted_28, _toDisplayString(__props.emptyText), 1)
                        ]),
                        _: 1
                      })),
                  (_unref(hasMore))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                        _createVNode(NeueButton, {
                          type: "secondary",
                          disabled: _unref(loadingMore),
                          onClick: _unref(loadMore)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(loadingMore) ? 'Loading...' : 'Show more'), 1)
                          ]),
                          _: 1
                        }, 8, ["disabled", "onClick"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 3
        }, 8, ["loading"]),
        _createElementVNode("div", _hoisted_30, [
          _createVNode(ReportDownloadHistory, {
            "report-type-filter": __props.downloadType,
            "button-label": "Download report",
            "date-range": downloadRange.value,
            "simplified-report-type": __props.downloadType
          }, null, 8, ["report-type-filter", "date-range", "simplified-report-type"])
        ])
      ])
    ]),
    _: 2
  }, [
    (_ctx.$slots.sidebar)
      ? {
          name: "sidebar",
          fn: _withCtx(() => [
            _renderSlot(_ctx.$slots, "sidebar")
          ]),
          key: "0"
        }
      : undefined
  ]), 1024))
}
}

}